<template>
    <div>
        <table class="table table-bordered">
            <thead>
            <tr>
                <th>File</th>
                <th>Type</th>
                <th v-if="editPermission" class="w-75s">
                </th>
                <th v-if="editPermission" class="w-50s text-center">
                    <input type="checkbox" v-model="isSelectAll" @change="selectAllChange"/>
                </th>
            </tr>
            </thead>
            <tbody class="v-middle">
            <tr v-for="(item, ind) in data.values">
                <!-- File Name Field -->
                <td>
                    <a :href="item.link">
                        <span>{{ item.file.name }}</span>
                    </a>
                    <i v-if="!item.valid" class="fa fa-exclamation-triangle text-danger"
                       data-toggle="tooltip"
                       title="This document appears to be corrupt or empty"></i>
                </td>
                <td>
                    <span>{{ item.type }}</span>
                </td>
                <td v-if="editPermission" class="text-center">
                  <adjuster-upload-edit-modal
                    :modal-id="'adjuster-upload-edit-modal-' + item.file.id"
                    :update-url="updateUrl"
                    :file-types="fileTypes"
                    :id="item.file.id"
                    :name="item.file.name"
                    :type="item.type_id"
                    :valid="item.valid"
                    @updated="updated">

                    <a href="#" class="btn btn-sm btn-primary btn-outline" v-modal="'adjuster-upload-edit-modal-' + item.file.id">
                      <i class="fa fa-pencil"></i> Edit
                    </a>
                  </adjuster-upload-edit-modal>
                </td>
                <td v-if="editPermission" class="text-center">
                    <input type="checkbox" :value="item.file.id" v-model="filesToDelete"/>
                </td>
            </tr>
            <tr v-if="editPermission">
                <td colspan="3"></td>
                <td>
                  <simple-confirmation-modal title="Delete Adjuster Attachments"
                                             :message="confirmationMessage"
                                             confirm-button-text="Delete"
                                             confirm-button-class="btn-danger"
                                             @confirm="onDeleteConfirm">
                    <a href="#" class="btn btn-danger" :class="{'disabled': filesToDelete.length === 0}" v-modal="'confirmation-modal'">
                      <i class="fa fa-trash"/>
                    </a>
                  </simple-confirmation-modal>
                </td>
            </tr>
            </tbody>
        </table>

        <hr class="hr-line-dashed"/>

        <slot name="cancel-button"/>

        <adjuster-upload-modal :upload-url="uploadUrl"
                               :file-types="fileTypes"
                               @uploaded="addLine">
        </adjuster-upload-modal>

        <modal id="error-modal" title="Error">
            {{ errorMessageModal }}
        </modal>
    </div>
</template>

<style>
    table {
        margin-bottom: 10px;
    }
</style>

<script>

  /**
   *
   * Component used to represent dynamic Attachments form.
   *
   *
   *
   * Example:
   *
   * <adjuster-attachments name="upload"
   *                       :edit-permission="{{ json_encode(auth()->user()->can('manage-adjusters')) }}"
   *                       :file-types="{{ $file_types }}"
   *                       upload-url="{{ route('adjusters.files.upload', ['adjuster' => $adjuster]) }}"
   *                       update-url="{{ route('adjusters.files.update', ['adjuster' => $adjuster]) }}"
   *                       delete-url="{{ route('adjusters.files.delete', ['adjuster' => $adjuster]) }}"
   *                       edit-permission="{{ route('adjusters.files.delete', ['adjuster' => $adjuster]) }}"
   *                       :model-value="{{ old("upload") ? collect(old("upload"))->toJson() : $value }}">
   * </adjuster-attachments>
   *
   */

  export default {

    props: {

      // Field name
      name: {
        type: String,
        required: true
      },

      // Initial Files array, should contain 'file' model and 'link'
      modelValue: {
        type: Array,
        default: () => {
          return [];
        }
      },

      // Array of file types for upload/edit modals.
      fileTypes: {
        type: Array,
        default: () => { return []; }
      },

      // Url route to upload files.
      uploadUrl: {
        type: String,
        required: true
      },

      // Url route to update files.
      updateUrl: {
        type: String,
        required: true
      },

      // Url route to delete files.
      deleteUrl: {
        type: String,
        required: true
      },

      // Does the user have permission to edit/upload attachments.
      editPermission: {
        type: Boolean,
        default: false
      }

    },

    created() {
    },

    data() {
      return {
        data: {
          // Files list.
          values: this.modelValue,
        },
        // Error message for modal dialog.
        errorMessageModal: '',
        // Array of file names to delete.
        filesToDelete: [],
        // true if all checkboxes selected, related to 'select all' checkbox in table header.
        isSelectAll: false,
      };
    },

    watch: {
      filesToDelete: {
        handler(newVal) {
          this.isSelectAll = newVal.length === this.data.values.length;
        },
        deep: true
      },

      'data.values': {
        handler(newVal) {
          this.isSelectAll = newVal.length === this.filesToDelete;
        },
        deep: true
      },

    },

    methods: {
      /**
       * On attachments delete confirmation in dialog.
       */
      onDeleteConfirm() {
        this.deleteFiles();
      },

      /**
       * Fill or clear filesToDelete array on 'select all' checkbox clicked.
       */
      selectAllChange() {
        this.filesToDelete.splice(0, this.filesToDelete.length);
        if (this.isSelectAll) {
          this.data.values.forEach((item) => {
            this.filesToDelete.push(item.file.id);
          });
        }
      },

      /**
       * Add line to files list after upload.
       */
      addLine(newFileData) {
        newFileData.valid = true;
        this.data.values.push(newFileData)
      },

      /**
       * Send request to delete selected files
       */
      deleteFiles() {
        let _this = this;

        let data = {
          '_token': this.$csrf_token,
          '_method': 'DELETE',
          'files': this.filesToDelete
        };

        axios.post(this.deleteUrl, data)
          .then(function (response) {
            _this.filesToDelete.forEach((file_id) => {
              _this.data.values.forEach((item, ind) => {
                if (item.file.id === file_id) {
                  _this.data.values.splice(ind, 1);
                  return false;
                }
              });
            });
            _this.filesToDelete.splice(0, _this.filesToDelete.length);
          })
          .catch(function (error) {
            if (error.response && error.response.status === 422) {
              _this.showErrorModal(error.response.data.message + ' Please check fields and resubmit form.');
            } else {
              _this.showErrorModal('Failed to send attachments. Please check fields and resubmit form.');
            }
          });
      },

      /**
       * Display error message.
       * @param errorMessage
       */
      showErrorModal(errorMessage) {
        this.errorMessageModal = errorMessage;
        // Programmatically show modal dialog.
        this.$modal.show('error-modal');
      },

      /**
       * Update attachment row after update request.
       * @param fileData
       */
      updated(fileData) {
        let findResult = this.data.values.find((item) => {
          return item.file.id === fileData.id;
        });
        findResult.type = fileData.type;
        findResult.type_id = fileData.type_id;
      },
    },

    computed: {
      // Confirmation message for modal dialog.
      confirmationMessage: function () {
        return 'Are you sure you want to delete ' + this.filesToDelete.length +
          ' attachment' + (this.filesToDelete.length > 1 ? 's' : '') + '?';
      }
    }
  }
</script>
